import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Input from '../components/atoms/Input';
import Button from '../components/atoms/Button';
import AvanttiIcon from '../components/icons/AvanttiIcon';
import { useResetPasswordMutation } from '../store';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';

interface FormData {
  new_password: string;
  confirm_password: string;
}

const validationSchema = yup.object().shape({
  new_password: yup
    .string()
    .required('Debes ingresar una contraseña')
    .min(6, 'La contraseña debe tener al menos 6 caracteres'),
  confirm_password: yup
    .string()
    .required('Debes confirmar tu contraseña')
    .oneOf([yup.ref('new_password')], 'Las contraseñas no coinciden'),
});

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigateWithQuery();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || searchParams.get('oobCode');
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const form = useForm<FormData>({
    defaultValues: {
      new_password: '',
      confirm_password: '',
    },
    resolver: yupResolver(validationSchema),
  });

  const { register, handleSubmit, formState } = form;

  const handleResetPassword = async (formData: FormData) => {
    if (!token) {
      setErrorMsg('Token de recuperación no válido. Por favor, solicita un nuevo enlace de recuperación.');
      return;
    }

    setErrorMsg('');
    setSuccessMsg('');

    try {
      await resetPassword({
        token,
        new_password: formData.new_password,
      }).unwrap();

      setSuccessMsg('Tu contraseña ha sido restablecida exitosamente. Redirigiendo al inicio de sesión...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (e) {
      console.error('Reset password error:', e);
      const error = e as { data?: { message?: string }; message?: string };
      if (error?.data?.message) {
        setErrorMsg(error.data.message);
      } else if (error?.message) {
        setErrorMsg(error.message);
      } else {
        setErrorMsg('Ocurrió un error al restablecer la contraseña. Por favor, intenta nuevamente.');
      }
    }
  };

  if (!token) {
    return (
      <div className='min-h-screen'>
        <div className="flex flex-col items-center admin p-6 my-10 rounded-3xl bg-blur bg-opacity-heavy shadow-sm max-w-[820px] mx-auto">
          <div className="flex flex-col items-center mt-10 mb-10">
            <div className="h-20 w-64">
              <AvanttiIcon className="w-full w-24 h-24 text-text-primary" />
            </div>
            <h1 className="text-2xl mt-8">Token inválido</h1>
            <p className="text-center mb-6">El enlace de recuperación no es válido o ha expirado.</p>
            <Button variant="primary" onClick={() => navigate('/login')}>
              Volver al inicio de sesión
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen'>
      <div className="flex flex-col items-center admin p-6 my-10 rounded-3xl bg-blur bg-opacity-heavy shadow-sm max-w-[820px] mx-auto">
        <div className="flex flex-col items-center mt-10">
          <div className="h-20 w-64">
            <AvanttiIcon className="w-full w-24 h-24 text-text-primary" />
          </div>
          <h1 className="text-2xl mt-8">Restablecer contraseña</h1>
          <p className="text-center mb-6">Ingresa tu nueva contraseña</p>
          <form className="flex flex-col gap-6 w-full max-w-[358px]" onSubmit={handleSubmit(handleResetPassword)}>
            <Input
              label="Nueva contraseña"
              labelAbove={true}
              id="new_password"
              type="password"
              {...register('new_password')}
              errMsg={formState.errors.new_password?.message}
            />
            <Input
              label="Confirmar contraseña"
              labelAbove={true}
              id="confirm_password"
              type="password"
              {...register('confirm_password')}
              errMsg={formState.errors.confirm_password?.message}
            />
            {errorMsg && <p className="text-error-600">{errorMsg}</p>}
            {successMsg && <p className="text-success-600">{successMsg}</p>}
            <div className="flex flex-col gap-2 mb-10">
              <Button variant="primary" type="submit" loading={isLoading}>
                Restablecer contraseña
              </Button>
              <Button
                variant="secondary"
                type="button"
                onClick={() => navigate('/login')}
              >
                Volver al inicio de sesión
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div >
  );
};

export default ResetPasswordPage;
